import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  GenerateGrowthSwotResponse,
  GrowthJourneyService,
  GrowthSwotLinkedArtifacts,
  SwotAssessment,
  SwotAssessmentDetail,
  SwotAssessmentHistoryItem
} from './growth-journey.service';
import {
  SwotStrategyLabel,
  normalizeDiscoveryStatus,
  swotProviderI18nKey,
  swotStrategyLabelI18nKey,
  swotStrategyLabelSeverity as resolveSwotStrategyLabelSeverity
} from './growth-status.util';

const SWOT_QUADRANTS = [
  'InternalStrength',
  'InternalWeakness',
  'ExternalOpportunity',
  'ExternalThreat'
] as const;

type SwotQuadrantType = (typeof SWOT_QUADRANTS)[number];

interface EditableSwotRow {
  swotFactorId?: string | null;
  factorTitle: string;
  factorType: string;
  relativeWeight: number;
  rank: number;
  rationale?: string | null;
}

interface ChartPoint {
  external: number;
  internal: number;
  isCurrent: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-growth-discovery-swot-panel',
  providers: [MessageService],
  imports: [
    FormsModule,
    RouterLink,
    AinFormFieldComponent,
    InfoHintComponent,
    AinStateComponent,
    ButtonModule,
    CheckboxModule,
    InputNumberModule,
    SelectModule,
    TagModule,
    TableModule
  ],
  template: `
    <section id="swot" class="swot-panel">
      <div class="section-title">
        <h3>{{ t('growth.journey.swot.panel.title') }}</h3>
      </div>

      <ain-info-hint
        [titleKey]="'growth.journey.swot.help.title'"
        [descriptionKey]="'growth.journey.swot.help.description'"
        [bulletKeys]="[
          'growth.journey.swot.help.bullets.draft',
          'growth.journey.swot.help.bullets.quadrant',
          'growth.journey.swot.help.bullets.approval'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (autoGenerateWhenEmpty && generating() && !assessment()) {
        <ain-state type="loading" [title]="t('growth.workflow.surfaces.journey.swot.autoGenerating')" />
      } @else {
        @if (providerDisplay(); as providerInfo) {
          <div class="meta-row">
            <p-tag [value]="providerLabel(providerInfo.provider)" severity="info" />
            @if (providerInfo.usedFallback) {
              <p-tag [value]="t('growth.journey.swot.fallbackUsed')" severity="warn" />
            }
          </div>
        }

        @if (artifacts(); as linked) {
          @if (linked.swot || linked.situationAssessment) {
            <div class="artifact-links">
              @if (linked.swot; as swot) {
                <a
                  pButton
                  severity="secondary"
                  [routerLink]="['/growth/agents/strategy-artifacts', swot.id]"
                  [label]="t('growth.journey.swot.artifacts.reviewSwot')"></a>
              }
              @if (linked.situationAssessment; as situation) {
                <a
                  pButton
                  severity="secondary"
                  [routerLink]="['/growth/agents/strategy-artifacts', situation.id]"
                  [label]="t('growth.journey.swot.artifacts.reviewSituation')"></a>
              }
            </div>
          }
        }

        <div class="toolbar-row">
          <a
            pButton
            severity="secondary"
            routerLink="/growth/journey/swot-factors"
            [label]="t('growth.journey.swot.factors.manage')"></a>
        </div>

        @if (canGenerate()) {
          <ain-form-field [label]="t('growth.journey.swot.fields.includeIntelligenceEnrich')">
            <p-checkbox [(ngModel)]="includeIntelligenceEnrich" [binary]="true" />
          </ain-form-field>

          <div class="actions">
            <button
              pButton
              type="button"
              [label]="t('growth.journey.swot.generateDraft')"
              [loading]="generating()"
              (click)="generateDraft()"></button>
          </div>
        }

        @if (assessment(); as item) {
          <div class="totals-row">
            <span class="meta-label">{{ t('growth.journey.swot.totals.internal') }}: {{ internalTotal().toFixed(2) }}</span>
            <span class="meta-label">{{ t('growth.journey.swot.totals.external') }}: {{ externalTotal().toFixed(2) }}</span>
            <span class="meta-label">{{ t('growth.journey.swot.fields.strategyLabel') }}</span>
            <p-tag
              [value]="t(swotStrategyLabelKey(derivedStrategyLabel()))"
              [severity]="swotStrategyLabelSeverity(derivedStrategyLabel())" />
          </div>

          <div class="chart-block">
            <h4>{{ t('growth.journey.swot.chart.title') }}</h4>
            <svg class="swot-chart" viewBox="0 0 100 100" role="img" [attr.aria-label]="t('growth.journey.swot.chart.title')">
              <rect x="10" y="10" width="80" height="80" fill="#f9fafb" stroke="#d1d5db" />
              <line x1="10" y1="50" x2="90" y2="50" stroke="#9ca3af" stroke-dasharray="3 2" />
              <line x1="50" y1="10" x2="50" y2="90" stroke="#9ca3af" stroke-dasharray="3 2" />
              <text x="72" y="18" class="chart-label">{{ t('growth.journey.swot.strategyLabel.aggressive') }}</text>
              <text x="14" y="18" class="chart-label">{{ t('growth.journey.swot.strategyLabel.competitive') }}</text>
              <text x="72" y="88" class="chart-label">{{ t('growth.journey.swot.strategyLabel.conservative') }}</text>
              <text x="14" y="88" class="chart-label">{{ t('growth.journey.swot.strategyLabel.defensive') }}</text>
              <text x="50" y="98" text-anchor="middle" class="chart-axis">{{ t('growth.journey.swot.chart.axisExternal') }}</text>
              <text x="4" y="50" text-anchor="middle" class="chart-axis" transform="rotate(-90 4 50)">{{ t('growth.journey.swot.chart.axisInternal') }}</text>
              @if (historyPolyline()) {
                <polyline [attr.points]="historyPolyline()" fill="none" stroke="#93c5fd" stroke-width="1.5" opacity="0.7" />
              }
              @for (point of chartPoints(); track $index) {
                @if (!point.isCurrent) {
                  <circle
                    [attr.cx]="plotX(point.external)"
                    [attr.cy]="plotY(point.internal)"
                    r="2"
                    fill="#93c5fd"
                    opacity="0.55" />
                }
              }
              @if (currentChartPoint(); as current) {
                <circle
                  [attr.cx]="plotX(current.external)"
                  [attr.cy]="plotY(current.internal)"
                  r="3.5"
                  fill="#2563eb"
                  stroke="#1e40af"
                  stroke-width="1" />
              }
            </svg>
            <div class="chart-legend">
              <span class="legend-item"><span class="legend-dot current"></span>{{ t('growth.journey.swot.chart.current') }}</span>
              <span class="legend-item"><span class="legend-dot history"></span>{{ t('growth.journey.swot.chart.history') }}</span>
            </div>
          </div>

          <div class="matrix-grid">
            @for (quadrant of quadrants; track quadrant) {
              <div class="quadrant-block">
                <h4>{{ t(factorTypeKey(quadrant)) }}</h4>
                <p-table [value]="rowsForQuadrant(quadrant)" styleClass="quadrant-table">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.swot.matrix.factor') }}</th>
                      <th>{{ t('growth.journey.swot.matrix.weight') }}</th>
                      <th>{{ t('growth.journey.swot.matrix.rank') }}</th>
                      <th>{{ t('growth.journey.swot.matrix.score') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.factorTitle }}</td>
                      <td>
                        <p-inputNumber
                          [(ngModel)]="row.relativeWeight"
                          [min]="0"
                          [max]="1"
                          [step]="0.05"
                          [disabled]="!isEditable()"
                          (ngModelChange)="onRowChanged()" />
                      </td>
                      <td>
                        <p-select
                          [options]="rankOptions"
                          optionLabel="label"
                          optionValue="value"
                          [(ngModel)]="row.rank"
                          [disabled]="!isEditable()"
                          appendTo="body"
                          (ngModelChange)="onRowChanged()" />
                      </td>
                      <td>{{ rowScore(row).toFixed(2) }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
            }
          </div>

          <div class="actions">
            @if (isEditable()) {
              <button
                pButton
                type="button"
                severity="secondary"
                [label]="t('growth.journey.swot.actions.save')"
                [loading]="saving()"
                (click)="saveDraft()"></button>
              <button
                pButton
                type="button"
                [label]="t('growth.journey.swot.actions.approve')"
                [loading]="approving()"
                (click)="approve()"></button>
            }
          </div>
        }
      }

      @if (actionError()) {
        <p class="action-error">{{ actionError() }}</p>
      }
    </section>
  `,
  styles: [`
    .swot-panel { border-top: 1px solid #e5e7eb; padding-top: 12px; display: grid; gap: 12px; }
    .section-title h3, .chart-block h4, .quadrant-block h4 { margin: 0; }
    .meta-row, .totals-row, .toolbar-row, .artifact-links, .actions, .chart-legend { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .meta-label { color: #6b7280; font-weight: 600; }
    .action-error { margin: 0; color: #b91c1c; }
    .matrix-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px; }
    .quadrant-block { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; display: grid; gap: 8px; }
    .chart-block { display: grid; gap: 8px; }
    .swot-chart { width: 100%; max-width: 320px; height: auto; }
    .chart-label { font-size: 5px; fill: #6b7280; }
    .chart-axis { font-size: 4.5px; fill: #374151; }
    .chart-legend { font-size: 12px; color: #4b5563; }
    .legend-item { display: inline-flex; align-items: center; gap: 4px; margin-inline-end: 12px; }
    .legend-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
    .legend-dot.current { background: #2563eb; }
    .legend-dot.history { background: #93c5fd; }
    :host ::ng-deep .quadrant-table .p-inputnumber,
    :host ::ng-deep .quadrant-table .p-select { width: 100%; max-width: 120px; }
  `]
})
export class GrowthDiscoverySwotPanelComponent implements OnInit, OnChanges {
  @Input({ required: true }) discoveryId!: string;
  @Input() discoveryStatus = '';
  @Input() autoGenerateWhenEmpty = false;
  @Input() readOnly = false;
  @Output() discoveryChanged = new EventEmitter<void>();

  private readonly journey = inject(GrowthJourneyService);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly quadrants = SWOT_QUADRANTS;
  readonly rankOptions = [0, 1, 2, 3, 4].map(value => ({ label: String(value), value }));

  readonly loading = signal(false);
  readonly generating = signal(false);
  readonly saving = signal(false);
  readonly approving = signal(false);
  readonly actionError = signal<string | null>(null);
  readonly artifacts = signal<GrowthSwotLinkedArtifacts | null>(null);
  readonly assessment = signal<SwotAssessment | null>(null);
  readonly history = signal<SwotAssessmentHistoryItem[]>([]);
  readonly lastApproveResult = signal<GenerateGrowthSwotResponse | null>(null);
  readonly editableRows = signal<EditableSwotRow[]>([]);

  readonly internalTotal = computed(() => this.sumScores(['InternalStrength', 'InternalWeakness']));
  readonly externalTotal = computed(() => this.sumScores(['ExternalOpportunity', 'ExternalThreat']));
  readonly derivedStrategyLabel = computed(() => this.deriveStrategyLabel(this.internalTotal(), this.externalTotal()));

  readonly providerDisplay = computed(() => {
    const approved = this.lastApproveResult();
    if (approved) {
      return { provider: approved.provider, usedFallback: approved.usedFallback };
    }

    const item = this.assessment();
    if (item) {
      return { provider: item.aiProvider, usedFallback: item.usedFallback };
    }

    return null;
  });

  readonly chartPoints = computed((): ChartPoint[] => {
    const sorted = [...this.history()].sort(
      (a, b) => new Date(a.assessedAtUtc).getTime() - new Date(b.assessedAtUtc).getTime()
    );

    return sorted.map(item => ({
      external: item.externalScore,
      internal: item.internalScore,
      isCurrent: false
    }));
  });

  readonly currentChartPoint = computed((): ChartPoint | null => {
    const item = this.assessment();
    if (!item) {
      return null;
    }

    return {
      external: this.externalTotal(),
      internal: this.internalTotal(),
      isCurrent: true
    };
  });

  readonly historyPolyline = computed((): string | null => {
    const points = this.chartPoints();
    if (points.length < 2) {
      return null;
    }

    return points.map(point => `${this.plotX(point.external)},${this.plotY(point.internal)}`).join(' ');
  });

  includeIntelligenceEnrich = false;
  private autoGenerateAttempted = false;
  private situationSummary: string | null = null;
  private keyInsights: string[] = [];

  ngOnInit(): void {
    this.loadAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['discoveryId'] && !changes['discoveryId'].firstChange) {
      this.autoGenerateAttempted = false;
      this.loadAll();
    }
  }

  canGenerate(): boolean {
    return !this.readOnly && normalizeDiscoveryStatus(this.discoveryStatus) === 'ReadyForSwot';
  }

  isEditable(): boolean {
    if (this.readOnly) {
      return false;
    }

    const status = this.assessment()?.status ?? '';
    return status !== 'Approved' && status !== 'Superseded';
  }

  factorTypeKey(factorType: SwotQuadrantType): string {
    const map: Record<SwotQuadrantType, string> = {
      InternalStrength: 'growth.journey.swot.factorType.internalStrength',
      InternalWeakness: 'growth.journey.swot.factorType.internalWeakness',
      ExternalOpportunity: 'growth.journey.swot.factorType.externalOpportunity',
      ExternalThreat: 'growth.journey.swot.factorType.externalThreat'
    };
    return map[factorType];
  }

  rowsForQuadrant(quadrant: SwotQuadrantType): EditableSwotRow[] {
    return this.editableRows().filter(row => row.factorType === quadrant);
  }

  rowScore(row: EditableSwotRow): number {
    return (row.relativeWeight ?? 0) * (row.rank ?? 0);
  }

  plotX(external: number): number {
    return 10 + (Math.min(Math.max(external, 0), 4) / 4) * 80;
  }

  plotY(internal: number): number {
    return 90 - (Math.min(Math.max(internal, 0), 4) / 4) * 80;
  }

  onRowChanged(): void {
    this.editableRows.set([...this.editableRows()]);
  }

  swotStrategyLabelKey(label: string): string {
    return swotStrategyLabelI18nKey(label);
  }

  swotStrategyLabelSeverity(label: string): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveSwotStrategyLabelSeverity(label);
  }

  providerLabel(provider: string): string {
    return this.t(swotProviderI18nKey(provider));
  }

  generateDraft(): void {
    if (!this.canGenerate() || this.generating()) {
      return;
    }

    this.generateDraftInternal(false);
  }

  saveDraft(): void {
    const item = this.assessment();
    if (!item || !this.isEditable() || this.saving()) {
      return;
    }

    this.saving.set(true);
    this.actionError.set(null);

    this.journey
      .saveSwotAssessment(item.id, {
        details: this.editableRows().map(row => ({
          swotFactorId: row.swotFactorId,
          factorTitle: row.factorTitle,
          factorType: row.factorType,
          relativeWeight: row.relativeWeight,
          rank: row.rank,
          rationale: row.rationale
        })),
        situationSummary: this.situationSummary,
        keyInsights: this.keyInsights
      })
      .subscribe({
        next: assessment => {
          this.applyAssessment(assessment);
          this.saving.set(false);
          this.messages.add({ severity: 'success', summary: this.t('growth.journey.swot.actions.save') });
          this.reloadHistoryAndArtifacts();
        },
        error: err => {
          this.saving.set(false);
          this.actionError.set(this.resolveActionError(err));
        }
      });
  }

  approve(): void {
    const item = this.assessment();
    if (!item || !this.isEditable() || this.approving()) {
      return;
    }

    this.approving.set(true);
    this.actionError.set(null);

    this.journey.approveSwotAssessment(item.id).subscribe({
      next: result => {
        this.lastApproveResult.set(result);
        this.approving.set(false);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.swot.approved') });
        this.discoveryChanged.emit();
        this.loadAll();
      },
      error: err => {
        this.approving.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private loadAll(): void {
    this.loading.set(true);
    this.actionError.set(null);

    forkJoin({
      artifacts: this.journey.getSwotArtifacts(this.discoveryId),
      latest: this.journey.getLatestSwotAssessment(this.discoveryId),
      history: this.journey.listSwotAssessments(this.discoveryId)
    }).subscribe({
      next: ({ artifacts, latest, history }) => {
        this.artifacts.set(artifacts);
        this.history.set(history);
        if (latest) {
          this.applyAssessment(latest);
          this.loading.set(false);
        } else {
          this.assessment.set(null);
          this.editableRows.set([]);
          this.tryAutoGenerateDraft();
        }
      },
      error: err => {
        this.loading.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  private tryAutoGenerateDraft(): void {
    if (!this.autoGenerateWhenEmpty || this.autoGenerateAttempted || this.readOnly) {
      this.loading.set(false);
      return;
    }

    this.autoGenerateAttempted = true;

    const attempt = (status: string) => {
      if (normalizeDiscoveryStatus(status) !== 'ReadyForSwot') {
        this.loading.set(false);
        return;
      }

      this.loading.set(false);
      this.generateDraftInternal(true);
    };

    if (this.discoveryStatus) {
      attempt(this.discoveryStatus);
      return;
    }

    this.journey.getDiscovery(this.discoveryId).subscribe({
      next: discovery => attempt(discovery.status),
      error: err => {
        this.loading.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  private generateDraftInternal(isAuto: boolean): void {
    if (this.generating()) {
      return;
    }

    this.generating.set(true);
    this.actionError.set(null);

    this.journey
      .generateSwotAssessmentDraft(this.discoveryId, {
        idempotencyKey: isAuto ? `swot-auto-${this.discoveryId}` : `swot-${this.discoveryId}-${Date.now()}`,
        includeIntelligenceEnrich: this.includeIntelligenceEnrich
      })
      .subscribe({
        next: result => {
          this.journey.getSwotAssessment(result.assessmentId).subscribe({
            next: assessment => {
              this.applyAssessment(assessment);
              this.generating.set(false);
              this.messages.add({ severity: 'success', summary: this.t('growth.journey.swot.generated') });
              this.discoveryChanged.emit();
              this.reloadHistoryAndArtifacts();
            },
            error: err => {
              this.generating.set(false);
              this.actionError.set(this.resolveActionError(err));
            }
          });
        },
        error: err => {
          this.generating.set(false);
          this.actionError.set(this.resolveActionError(err));
          if (isAuto) {
            this.messages.add({
              severity: 'error',
              summary: this.t('growth.workflow.surfaces.journey.swot.autoGenerateFailed')
            });
          }
        }
      });
  }

  private reloadHistoryAndArtifacts(): void {
    forkJoin({
      artifacts: this.journey.getSwotArtifacts(this.discoveryId),
      history: this.journey.listSwotAssessments(this.discoveryId),
      latest: this.journey.getLatestSwotAssessment(this.discoveryId)
    }).subscribe({
      next: ({ artifacts, history, latest }) => {
        this.artifacts.set(artifacts);
        this.history.set(history);
        if (latest) {
          this.applyAssessment(latest);
        }
      },
      error: err => {
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  private applyAssessment(assessment: SwotAssessment): void {
    this.assessment.set(assessment);
    this.situationSummary = assessment.situationSummary ?? null;
    this.keyInsights = [...(assessment.keyInsights ?? [])];
    this.editableRows.set(assessment.details.map(detail => this.toEditableRow(detail)));
  }

  private toEditableRow(detail: SwotAssessmentDetail): EditableSwotRow {
    return {
      swotFactorId: detail.swotFactorId,
      factorTitle: detail.factorTitle,
      factorType: detail.factorType,
      relativeWeight: detail.relativeWeight,
      rank: detail.rank,
      rationale: detail.rationale
    };
  }

  private sumScores(factorTypes: string[]): number {
    return this.editableRows()
      .filter(row => factorTypes.includes(row.factorType))
      .reduce((sum, row) => sum + this.rowScore(row), 0);
  }

  private deriveStrategyLabel(internal: number, external: number): SwotStrategyLabel {
    if (internal > 2.5 && external > 2.5) {
      return 'Aggressive';
    }

    if (internal > 2.5 && external <= 2.5) {
      return 'Competitive';
    }

    if (internal <= 2.5 && external > 2.5) {
      return 'Conservative';
    }

    return 'Defensive';
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code === 'growth_discovery_not_ready_for_swot') {
      return this.t('growth.journey.swot.errors.notReady');
    }

    if (code === 'growth_discovery_not_found') {
      return this.t('common.notFound');
    }

    if (code === 'growth_swot_assessment_not_editable') {
      return this.t('growth.journey.swot.errors.notEditable');
    }

    if (code === 'growth_swot_assessment_empty') {
      return this.t('growth.journey.swot.errors.empty');
    }

    return code ?? this.t('common.error');
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    if (!body) {
      return null;
    }

    if (typeof body['error'] === 'string') {
      return body['error'];
    }

    if (typeof body['Error'] === 'string') {
      return body['Error'];
    }

    return null;
  }
}
